// LibraryPage.jsx — Task 9. The agent library: browse the curated quick-tasks + agent-jobs catalog,
// filter by search + use-case, expand/collapse sections, expand a card for detail, and launch.
//
// ONE honest catalog powers three surfaces (dock, slider, this page). This page fetches the
// token-scoped read API (GET /api/agent/companies/:slug/library) and, on any failure, falls back to
// the bundled registry (getRegistry) with the gate treated as enabled — the server still 403s
// protectively on any real job create, so the fallback never leaks an action. Dock + slider never
// fetch (bundled import only); only this page does.
//
// Launch wiring (stage the descriptor, then navigate into agent activity) lands in C5 — this commit
// renders the catalog, search, filters, and expand/collapse.

import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
  getRegistry, buildLibrarySections, filterEntries, channelInfo, launchDescriptor, cardCtaState,
} from "../shell/libraryViewModel.js"
import { stageLaunch } from "../shell/libraryLaunch.js"
// Item #7: the return leg — a View-all entry staged a {chatId, activityView} descriptor; consume it
// on mount (read+remove — a stale one can never resurface the button on a later organic visit) to
// show a back button that restores the exact agent-shell state the user left.
import { consumeReturn, stageRestore } from "../shell/libraryReturn.js"

const KIND_LABEL = Object.freeze({ task: "Quick task", job: "Agent job" })
// Item #6 unification: EVERY card now opens the same full agent-job form (a quick task seeds it
// with the chat-invoked kind), so the task CTA no longer claims an instant chat run.
const CTA_LABEL = Object.freeze({ task: "Set up this task", job: "Set up this job" })

// The launch target — a full Rails page load into the two-mode shell in agent mode (a fresh mount so
// the shell's consume effect, C5, fires). Uses the FLAT /activity form — the canonical activity
// target every other handoff (nav toggle, m3-15, artifact-rerun) navigates to. (The nested
// /companies/:slug/activity form is ALSO client-routed since the 2026-07-28 repair round — it used
// to fall through to BridgeLoader — but the flat form stays canonical here.) The shell resolves
// companySlug from the react-root mount node, so the flat form lands in the right company's agent
// surface.
export function activityUrl() {
  return "/activity?mode=agent"
}

function chTint(color) {
  return `color-mix(in srgb, ${color} 13%, transparent)`
}

function LibCard({ entry, gateOn, expanded, onToggle, onLaunch }) {
  const info = channelInfo(entry.channel)
  // Item #6 gate-awareness (BC-8): the gate keys off the LAUNCH TARGET — every card opens the
  // agent-job builder now, so with the gate off EVERY card (task + job) gets the disabled CTA.
  const ctaState = cardCtaState(entry, gateOn)
  const comingSoon = ctaState === "coming_soon"
  const launchBlocked = ctaState === "gated"
  return (
    <div className={`lib-card${expanded ? " open" : ""}${comingSoon ? " lib-card--soon" : ""}`} data-entry={entry.id}>
      <button type="button" className="lib-card-hd" aria-expanded={expanded} onClick={onToggle}>
        <span className="lib-card-badges">
          <span className="lib-badge lib-badge--ch" style={{ color: info.color, background: chTint(info.color) }}>{info.label}</span>
          <span className="lib-badge lib-badge--kind">{KIND_LABEL[entry.kind]}</span>
          {comingSoon && <span className="lib-badge lib-badge--soon">Coming soon</span>}
        </span>
        <span className="lib-card-title">{entry.title}</span>
        <span className="lib-card-pitch">{entry.pitch}</span>
      </button>
      {expanded && (
        <div className="lib-card-body">
          {entry.output && <p className="lib-card-output">{entry.output}</p>}
          {entry.note && <p className="lib-card-note">{entry.note}</p>}
          {comingSoon ? (
            <div className="lib-soon-line">Coming soon — not available in Vesta yet.</div>
          ) : launchBlocked ? (
            <button type="button" className="lib-cta lib-cta--off" disabled>
              Ask your Vesta admin to enable Agent Jobs
            </button>
          ) : (
            <button type="button" className={`lib-cta${entry.kind === "job" ? " lib-cta--job" : ""}`} onClick={() => onLaunch(entry)}>
              {CTA_LABEL[entry.kind]}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function LibSection({ section, collapsed, onToggle, gateOn, openCard, onToggleCard, onLaunch }) {
  return (
    <section className="lib-section">
      <button type="button" className="lib-section-hd" aria-expanded={!collapsed} onClick={onToggle}>
        <span className={`lib-chevron${collapsed ? "" : " open"}`} aria-hidden="true">▾</span>
        <span className="lib-section-title">{section.category}</span>
        <span className="lib-section-count">{section.entries.length}</span>
      </button>
      {!collapsed && (
        <div className="lib-section-cards">
          {section.entries.map((e) => (
            <LibCard
              key={`${section.key}-${e.id}`}
              entry={e}
              gateOn={gateOn}
              expanded={openCard === `${section.key}-${e.id}`}
              onToggle={() => onToggleCard(`${section.key}-${e.id}`)}
              onLaunch={onLaunch}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default function LibraryPage({ companySlug }) {
  const params = useParams()
  const slug = companySlug || params.companySlug || null

  const [data, setData] = useState(null) // { entries, categories, use_cases, gates }
  const [q, setQ] = useState("")
  const [useCase, setUseCase] = useState("All")
  const [openCard, setOpenCard] = useState(null)
  const [collapsedByKey, setCollapsedByKey] = useState({})

  // Item #7: the return descriptor, consumed ONCE at mount (lazy initializer — read+remove).
  // Present only when this page load came from a View-all entry point.
  const [returnDesc] = useState(() => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    return consumeReturn(store)
  })

  // Item #7 taste (iii): a refresh / direct load of the agent-chrome library (?mode=agent stamps
  // data-mode="agent" on the mount node) has no descriptor — the consume already happened on the
  // previous load, or there never was one. The user is still visibly INSIDE the agent experience,
  // so a fallback "Back to AI Agent" button renders (plain navigation to /activity?mode=agent; the
  // chat pane's sessionStorage mirror restore reopens whatever chat was active). On a Standard-
  // chrome /library visit nothing renders, exactly as before.
  const [agentChrome] = useState(() => {
    try {
      const node = document.getElementById("react-root")
      return !!node && node.getAttribute("data-mode") === "agent"
    } catch (_e) {
      return false
    }
  })

  // Back → re-stage the descriptor under the RESTORE key, then a full navigation to the FLAT
  // /activity?mode=agent (the only route that mounts the agent shell — the nested form never does).
  // The shell's mount-time consume effect restores the panel view and re-dispatches the open chat.
  const handleBack = () => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    stageRestore(store, returnDesc)
    window.location.assign(activityUrl())
  }

  useEffect(() => {
    let live = true
    const fallback = () => {
      const reg = getRegistry()
      return { entries: reg.entries, categories: reg.categories, use_cases: reg.use_cases, gates: { agent_triggers: true } }
    }
    if (!slug) {
      setData(fallback())
      return undefined
    }
    fetch(`/api/agent/companies/${slug}/library`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        if (live) {
          setData({
            entries: d.entries || [],
            categories: d.categories || [],
            use_cases: d.use_cases || [],
            gates: d.gates || { agent_triggers: false },
          })
        }
      })
      .catch(() => {
        if (live) setData(fallback())
      })
    return () => {
      live = false
    }
  }, [slug])

  const registryLike = useMemo(
    () => (data
      ? { entries: data.entries, categories: data.categories, use_cases: data.use_cases }
      : { entries: [], categories: [], use_cases: [] }),
    [data]
  )

  const filtered = useMemo(
    () => filterEntries(registryLike.entries, { query: q, useCase }),
    [registryLike, q, useCase]
  )
  const sections = useMemo(
    () => buildLibrarySections({ entries: filtered, categories: registryLike.categories }),
    [filtered, registryLike]
  )

  const gateOn = data ? !!data.gates.agent_triggers : true
  const searching = q.trim().length > 0

  // Default: Featured (idx 0) + the first category (idx 1) expanded, the rest collapsed. A live
  // search overrides collapse so matches in any section are always visible (V2: filter ≠ scroll).
  const isCollapsed = (key, idx) => {
    if (searching) return false
    if (key in collapsedByKey) return collapsedByKey[key]
    return idx >= 2
  }
  const toggleSection = (key, idx) =>
    setCollapsedByKey((m) => ({ ...m, [key]: !(key in m ? m[key] : idx >= 2) }))

  const toggleCard = (id) => setOpenCard((cur) => (cur === id ? null : id))

  // D-T9-g: stage the launch descriptor in sessionStorage, then navigate into agent activity (a full
  // Rails page load so the shell's mount-time consume effect fires). Item #6: EVERY entry is a job
  // descriptor now — a quick task seeds the same full NewAgentForm with kind:'chat' pre-selected.
  const handleLaunch = (entry) => {
    let store = null
    try { store = window.sessionStorage } catch (_e) { store = null }
    stageLaunch(store, launchDescriptor(entry))
    window.location.assign(activityUrl())
  }

  const useCases = ["All", ...(registryLike.use_cases || [])]
  const nothing = data && sections.length === 0

  return (
    <div className="lib" data-page="library">
      <div className="lib-wrap">
        <header className="lib-head">
          {returnDesc ? (
            <button type="button" className="lib-back" onClick={handleBack}>
              <span className="lib-back-arrow" aria-hidden="true">←</span>
              {returnDesc.chatId ? "Back to your chat" : "Back to AI Agent"}
            </button>
          ) : agentChrome ? (
            /* Item #7 taste (iii): no descriptor but agent chrome — the fallback still gets the
               user home to the agent shell instead of stranding them. */
            <button type="button" className="lib-back" onClick={() => window.location.assign(activityUrl())}>
              <span className="lib-back-arrow" aria-hidden="true">←</span>
              Back to AI Agent
            </button>
          ) : null}
          <h1 className="lib-title">Task library</h1>
          <p className="lib-sub">Everything Vesta can do for you — quick tasks to run now and agent jobs to run on their own. Browse, then launch.</p>
        </header>

        <div className="lib-tools">
          <div className="lib-search">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the library"
              aria-label="Search the library"
            />
          </div>
          <div className="lib-chips" role="tablist" aria-label="Filter by use case">
            {useCases.map((uc) => (
              <button
                key={uc}
                type="button"
                role="tab"
                aria-selected={useCase === uc}
                className={`lib-chip${useCase === uc ? " on" : ""}`}
                onClick={() => setUseCase(uc)}
              >
                {uc}
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          <div className="lib-note" role="status">Loading the library…</div>
        ) : nothing ? (
          <div className="lib-empty">
            <h3>Nothing matches</h3>
            <p>No tasks or jobs match that search and filter.</p>
            <button type="button" className="lib-cta" onClick={() => { setQ(""); setUseCase("All") }}>
              Clear search &amp; filters
            </button>
          </div>
        ) : (
          <div className="lib-sections">
            {sections.map((section, idx) => (
              <LibSection
                key={section.key}
                section={section}
                collapsed={isCollapsed(section.key, idx)}
                onToggle={() => toggleSection(section.key, idx)}
                gateOn={gateOn}
                openCard={openCard}
                onToggleCard={toggleCard}
                onLaunch={handleLaunch}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
